"use client";

import React, { useState } from "react";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { getImagePath } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { formatNumberByLocale } from "@/utils/numberFormatter";

const page = () => {
  const t = useTranslations("faqs");
  const locale = useLocale();

  const partnerFaqs = Array.from({ length: 10 }, (_, i) => ({
    q: t(`p_q${i + 1}`),
    a: t(`p_a${i + 1}`),
  }));

  // Collect q/a pairs while the keys exist, so each language can have its own count.
  const collect = (prefix: string, start = 1) => {
    const out: { q: string; a: string }[] = [];
    for (let i = start; t.has(`${prefix}_q${i}`); i++) {
      out.push({ q: t(`${prefix}_q${i}`), a: t(`${prefix}_a${i}`) });
    }
    return out;
  };

  const producerFaqs = collect("pr");

  const villageFaqs = Array.from({ length: 9 }, (_, i) => ({
    q: t(`v_q${i + 1}`),
    a: t(`v_a${i + 1}`),
  }));

  // Own & Earn layout (en/mr/hi): For Customers · Own & Earn · For Producers.
  // Other languages keep the old Partners · Producers · Village Users tabs.
  const faqTabs = t.has("ownEarnLabel")
    ? [
        { label: t("villageLabel"), faqs: villageFaqs },
        { label: t("ownEarnLabel"), faqs: collect("oe") },
        { label: t("producerLabel"), faqs: producerFaqs },
      ]
    : [
        { label: t("partnerLabel"), faqs: partnerFaqs },
        { label: t("producerLabel"), faqs: producerFaqs },
        { label: t("villageLabel"), faqs: villageFaqs },
      ];

  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={t("pagename")}
        description=""
        bgImages={[getImagePath("/images/breadcrumb/howItWorks.webp")]}
        bgIndex={0}
      />

      <div className="container px-6 py-10">
        <div className="flex flex-col md:flex-row text-gray-700 text-lg leading-relaxed">
          {faqTabs.map((tab, idx) => (
            <button
              key={idx}
              onClick={() => setActiveTab(idx)}
              className={`bg-transparent w-auto md:w-full border border-gray-300 h-12 md:h-16 mb-5 hover:bg-transparent hover:text-[#003399]
                  ${
                    activeTab === idx
                      ? "text-[#003399] border-[#003399]"
                      : "text-[#C9C9C9] border-gray-300"
                  }
                  font-semibold text-lg transition-colors duration-200`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="space-y-6">
          {faqTabs[activeTab].faqs.map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              {/* Multilanguage Number (VERY IMPORTANT) */}
              <span className="text-lg font-bold ">
                {formatNumberByLocale(locale, i + 1)}.
              </span>

              <div>
                <span className="font-bold mb-2 text-lg">{item.q}</span>
                <p>{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default page;
